import React, { useState, useEffect } from 'react';
import { 
  User, 
  Globe, 
  Calendar, 
  Download,
  Link,
  Copy,
  Trash2,
  FileDown,
  Check,
  ExternalLink
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { format } from 'date-fns';

const SettingsPage = () => {
  const { api, user, updateProfile } = useAuth();
  const [loading, setLoading] = useState(true);
  const [settings, setSettings] = useState(null);
  const [icalTokens, setIcalTokens] = useState([]);
  const [timezones, setTimezones] = useState([]);
  const [profileData, setProfileData] = useState({ name: '', timezone: '' });
  const [saving, setSaving] = useState(false);
  const [copiedId, setCopiedId] = useState(null);
  // Subscription URLs are stored in localStorage (keyed by token ID) so they persist after page reload
  const [tokenUrls, setTokenUrls] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem('ical_token_urls') || '{}');
    } catch {
      return {};
    }
  });

  // For subscription URLs, use current origin if REACT_APP_BACKEND_URL is not set
  const API_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;

  useEffect(() => {
    fetchSettings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchSettings = async () => {
    try {
      const [settingsRes, timezonesRes] = await Promise.all([
        api.get('/settings'),
        api.get('/settings/timezones')
      ]);
      setSettings(settingsRes.data);
      setIcalTokens(settingsRes.data.icalTokens || []);
      setTimezones(timezonesRes.data.timezones || []);
      setProfileData({
        name: settingsRes.data.user.name,
        timezone: settingsRes.data.user.timezone
      });
    } catch (err) {
      toast.error('Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveProfile = async () => {
    setSaving(true);
    try {
      await updateProfile(profileData);
      toast.success('Profile updated');
    } catch (err) {
      toast.error('Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  const saveTokenUrl = (tokenId, url) => {
    const updated = { ...tokenUrls, [tokenId]: url };
    setTokenUrls(updated);
    try {
      localStorage.setItem('ical_token_urls', JSON.stringify(updated));
    } catch { /* storage unavailable */ }
  };

  const removeTokenUrl = (tokenId) => {
    const { [tokenId]: _removed, ...rest } = tokenUrls;
    setTokenUrls(rest);
    try {
      localStorage.setItem('ical_token_urls', JSON.stringify(rest));
    } catch { /* storage unavailable */ }
  };

  const handleCreateIcalToken = async (scope, projectType) => {
    try {
      const res = await api.post('/ical/tokens', {
        scope,
        projectType: projectType || null
      });

      const newToken = res.data.token;
      const plainToken = res.data.plainToken;
      const subscriptionUrl = `${API_URL}/api/ical/${plainToken}.ics`;

      setIcalTokens([newToken, ...icalTokens]);
      saveTokenUrl(newToken.id, subscriptionUrl);

      // Try to copy to clipboard
      try {
        await navigator.clipboard.writeText(subscriptionUrl);
        setCopiedId(newToken.id);
        setTimeout(() => setCopiedId(null), 3000);
        toast.success('Subscription link created and copied!');
      } catch (clipboardErr) {
        toast.success('Subscription link created! Click Copy to copy the link.');
      }
    } catch (err) {
      console.error('Failed to create token:', err);
      toast.error('Failed to create subscription link');
    }
  };

  const handleCopySubscriptionUrl = async (tokenId) => {
    const subscriptionUrl = tokenUrls[tokenId];
    if (!subscriptionUrl) {
      toast.error('Link not available. Please revoke and create a new subscription link.');
      return;
    }

    try {
      await navigator.clipboard.writeText(subscriptionUrl);
      setCopiedId(tokenId);
      setTimeout(() => setCopiedId(null), 3000);
      toast.success('Subscription link copied!');
    } catch (err) {
      toast.info('Copy this link: ' + subscriptionUrl);
    }
  };

  const handleRevokeToken = async (tokenId) => {
    try {
      await api.delete(`/ical/tokens/${tokenId}`);
      setIcalTokens(icalTokens.filter(t => t.id !== tokenId));
      removeTokenUrl(tokenId);
      toast.success('Subscription link revoked');
    } catch (err) {
      toast.error('Failed to revoke link');
    }
  };

  const handleDownloadCalendar = async (scope, projectType) => {
    try {
      let url = '/ical/download';
      if (projectType) {
        url += `?projectType=${projectType}`;
      }
      
      const response = await api.get(url, { responseType: 'blob' });
      const downloadUrl = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.setAttribute('download', `coursework-${scope || 'all'}-deadlines.ics`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Calendar downloaded');
    } catch (err) {
      toast.error('Failed to download calendar');
    }
  };

  const handleExportData = async () => {
    try {
      const response = await api.get('/settings/export', { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'coursework-export.json');
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Data exported');
    } catch (err) {
      toast.error('Failed to export data');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-pulse text-muted-foreground">Loading settings...</div>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto animate-fade-in" data-testid="settings-page">
      {/* Header */}
      <div className="mb-8">
        <h1 className="font-heading text-3xl font-bold text-slate-900 mb-2">Settings</h1>
        <p className="text-slate-600">Manage your account and preferences</p>
      </div>

      <div className="space-y-6">
        {/* Profile */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <User className="w-5 h-5 text-indigo-600" />
              Profile
            </CardTitle>
            <CardDescription>Your account information</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="name">Name</Label>
                <Input
                  id="name"
                  value={profileData.name}
                  onChange={(e) => setProfileData({ ...profileData, name: e.target.value })}
                  data-testid="settings-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  value={user?.email || ''}
                  disabled
                  className="bg-slate-50"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="timezone">Timezone</Label>
              <Select 
                value={profileData.timezone}
                onValueChange={(v) => setProfileData({ ...profileData, timezone: v })}
              >
                <SelectTrigger data-testid="settings-timezone-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {timezones.map((tz) => (
                    <SelectItem key={tz} value={tz}>{tz}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Button onClick={handleSaveProfile} disabled={saving} data-testid="save-profile-btn">
              {saving ? 'Saving...' : 'Save Changes'}
            </Button>
          </CardContent>
        </Card>

        {/* Calendar Export */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Calendar className="w-5 h-5 text-indigo-600" />
              Calendar Export
            </CardTitle>
            <CardDescription>
              Export your deadlines to Google Calendar, Apple Calendar, or Outlook
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Download Options */}
            <div>
              <h3 className="text-sm font-medium text-slate-900 mb-3">Download .ics File</h3>
              <p className="text-sm text-slate-500 mb-3">
                Download a calendar file you can import into any calendar app.
              </p>
              <div className="flex flex-wrap gap-2">
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => handleDownloadCalendar('all')}
                  data-testid="download-all-ics"
                >
                  <Download className="w-4 h-4 mr-2" />
                  All Deadlines
                </Button>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => handleDownloadCalendar('ee', 'EE')}
                >
                  <Download className="w-4 h-4 mr-2" />
                  EE Only
                </Button>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => handleDownloadCalendar('ia', 'IA')}
                >
                  <Download className="w-4 h-4 mr-2" />
                  IA Only
                </Button>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => handleDownloadCalendar('tok', 'TOK_ESSAY')}
                >
                  <Download className="w-4 h-4 mr-2" />
                  TOK Only
                </Button>
              </div>
            </div>

            {/* Subscription Links */}
            <div>
              <h3 className="text-sm font-medium text-slate-900 mb-3">Subscription Links</h3>
              <p className="text-sm text-slate-500 mb-4">
                Create a subscription link that automatically syncs with your calendar app. 
                When you add or update deadlines, they'll appear in your calendar automatically.
              </p>
              
              <div className="flex flex-wrap gap-2 mb-4">
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => handleCreateIcalToken('ALL')}
                  data-testid="create-subscription-all"
                >
                  <Link className="w-4 h-4 mr-2" />
                  Create All Deadlines Link
                </Button>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => handleCreateIcalToken('TYPE', 'EE')}
                >
                  <Link className="w-4 h-4 mr-2" />
                  Create EE Link
                </Button>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => handleCreateIcalToken('TYPE', 'IA')}
                >
                  <Link className="w-4 h-4 mr-2" />
                  Create IA Link
                </Button>
              </div>

              {icalTokens.length > 0 && (
                <div className="space-y-2">
                  <Label className="text-xs uppercase text-slate-500">Active Subscriptions</Label>
                  {icalTokens.map((token) => (
                    <div
                      key={token.id}
                      className="flex items-center justify-between p-3 bg-slate-50 rounded-lg"
                    >
                      <div className="flex-1 min-w-0">
                        <Badge variant="outline" className="mb-1">
                          {token.scope === 'ALL' ? 'All Deadlines' :
                           token.scope === 'TYPE' ? `${token.projectType} Only` :
                           'Project'}
                        </Badge>
                        <p className="text-xs text-slate-500">
                          Created {format(new Date(token.createdAt), 'MMM d, yyyy')}
                        </p>
                        {tokenUrls[token.id] && (
                          <p className="text-xs text-slate-400 mt-1 truncate max-w-xs font-mono">
                            {tokenUrls[token.id]}
                          </p>
                        )}
                      </div>
                      <div className="flex items-center gap-1 ml-2 shrink-0">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleCopySubscriptionUrl(token.id)}
                          className={copiedId === token.id ? 'text-green-600' : ''}
                          disabled={!tokenUrls[token.id]}
                          title={tokenUrls[token.id] ? 'Copy subscription URL' : 'URL not available — revoke and recreate'}
                        >
                          {copiedId === token.id ? (
                            <>
                              <Check className="w-4 h-4 mr-1" />
                              Copied
                            </>
                          ) : (
                            <>
                              <Copy className="w-4 h-4 mr-1" />
                              Copy
                            </>
                          )}
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-red-600 hover:bg-red-50"
                          onClick={() => handleRevokeToken(token.id)}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Instructions */}
            <div className="p-4 bg-indigo-50 rounded-xl">
              <h4 className="font-medium text-indigo-900 mb-2">How to add to your calendar</h4>
              <div className="text-sm text-indigo-700 space-y-2">
                <p><strong>Google Calendar:</strong> Settings → Add calendar → From URL → Paste subscription link</p>
                <p><strong>Apple Calendar:</strong> File → New Calendar Subscription → Paste link</p>
                <p><strong>Outlook:</strong> Add calendar → Subscribe from web → Paste link</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Data & Privacy */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileDown className="w-5 h-5 text-indigo-600" />
              Data & Privacy
            </CardTitle>
            <CardDescription>Export or manage your data</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <Button variant="outline" onClick={handleExportData} data-testid="export-data-btn">
              <Download className="w-4 h-4 mr-2" />
              Export All Data (JSON)
            </Button>
            <p className="text-sm text-slate-500">
              Download a copy of all your projects, deadlines, sources, and notes.
            </p>
          </CardContent>
        </Card>

        {/* Stats */}
        <Card>
          <CardHeader>
            <CardTitle>Your Stats</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-3 gap-4">
              <div className="text-center p-4 bg-slate-50 rounded-xl">
                <p className="text-2xl font-bold text-slate-900">{settings?.stats?.projects || 0}</p>
                <p className="text-sm text-slate-500">Projects</p>
              </div>
              <div className="text-center p-4 bg-slate-50 rounded-xl">
                <p className="text-2xl font-bold text-slate-900">{settings?.stats?.deadlines || 0}</p>
                <p className="text-sm text-slate-500">Deadlines</p>
              </div>
              <div className="text-center p-4 bg-slate-50 rounded-xl">
                <p className="text-2xl font-bold text-slate-900">{settings?.stats?.sources || 0}</p>
                <p className="text-sm text-slate-500">Sources</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default SettingsPage;
